'use client'
import { useGetIndividualCompanyDetailsQuery } from '@/app/globalRedux/api/individualCompanyService'
import { RootState } from '@/app/globalRedux/store'
import useLocalizedRouter from '@/app/hooks/useLocalizedRouter'
import { useTranslation } from '@/app/i18n/client'
import {
  CompanyDetails,
  CompanyInfo,
} from '@/app/types/individualCompanyProfile'
import { updatedAtDateFormat } from '@/app/utils/dateFormate'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { handleUrlClick } from '@/lib/utils'
import Image from 'next/image'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import CustomErrorBoundary from '../../ErrorBoundary'
import {
  setIsBeneficiary,
  setAccessCount,
  setAccessPer,
} from '@/app/globalRedux/userStore/userStore'
const CompanyDetailCard: React.FC<{
  id: string
  companyIndustryType: string
}> = ({ id, companyIndustryType }) => {
  const pushWithLocale = useLocalizedRouter()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { data, isError } = useGetIndividualCompanyDetailsQuery<{
    data: CompanyDetails
    isError: boolean
  }>(id)
  const [companyDetails, setCompanyDetails] = useState<CompanyInfo>({
    companyName: '',
    logo: '',
    companyAddress: '',
    companyCity: '',
    zipCode: '',
    companyUrl: '',
    friendlyUrl: '',
    countryName: '',
    joinedDate: '',
    feedback: '',
    certified: '',
    createdDate: '',
    updatedAt: '',
    employees: '',
    turnover: '',
    registrationNumber: '',
    languages: [],
    subscriptionIcon: '',
  })

  useEffect(() => {
    if (data && !isError) {
      setCompanyDetails({
        ...companyDetails,
        companyName: data?.company?.name,
        companyAddress: data?.company?.address?.street_address,
        companyCity: data?.company?.address?.city?.name,
        zipCode: data?.company?.address?.zip,
        companyUrl: data?.company?.company_url,
        friendlyUrl: data?.company?.friendly_url,
        countryName: data?.company?.stats?.origin,
        joinedDate: data?.company?.stats?.joined_at,
        feedback: data?.company?.stats?.feedback,
        certified: data?.company?.stats?.certified
          ? 'Certified'
          : 'Not Certified',
        updatedAt: data?.company?.stats?.updated_at,
        createdDate: data?.company?.additional_info?.year_of_start,
        employees: data?.company?.additional_info?.employees,
        turnover: data?.company?.additional_info?.turnover,
        registrationNumber: data?.company?.additional_info?.registration_number,
        languages: data?.company?.additional_info?.languages,
        logo: data?.company?.logo,
        subscriptionIcon: data?.company?.subscription?.name,
      })
      dispatch(setIsBeneficiary(data?.beneficiary))
      dispatch(setAccessCount(data?.access_count))
      dispatch(setAccessPer(data?.accessPer))
    }
  }, [data])
  const userCompany = useSelector((state: RootState) => state.user.companyName)

  return (
    <div>
      <div className="shadow-[0_0_6px_0_rgba(0,0,0,0.09)] w-[299px] rounded-[5px] relative bg-[#fff] h-fit -top-[3.8rem] mobile:top-2 mobile-width">
        <div className="relative">
          <div className="p-[25px] flex flex-col gap-6 mobile:gap-[5px]">
            <div className="w-full flex justify-center items-center">
              <div
                className={`w-[143px] h-[143px] ${companyDetails?.logo ? '' : 'bg-orange_tag'} rounded-sm overflow-hidden`}
              >
                <Image
                  loading="lazy"
                  width={143}
                  height={143}
                  src={
                    companyDetails?.logo ?? '/assets/Icons/noImage/noImage2.svg'
                  }
                  alt=""
                  className={`w-full h-full ${companyDetails?.logo ? 'object-contain' : 'object-cover'}`}
                />
              </div>
            </div>
            <div className="flex flex-col gap-5 mobile:gap-[0px]">
              <div className="text-[16px] flex flex-col font-ns_bold gap-[2px] ">
                {companyDetails?.companyName ? (
                  <p>{companyDetails?.companyName}</p>
                ) : (
                  <Skeleton className="w-40 h-5" />
                )}
                {companyDetails?.updatedAt ? (
                  <p className="text-xs text-[#808080]">
                    {t('Last Update')}:{' '}
                    {updatedAtDateFormat(companyDetails?.updatedAt)}
                  </p>
                ) : (
                  <Skeleton className="w-full h-5" />
                )}
              </div>
              <div className="flex flex-col gap-2 mobile:gap-1">
                <p className="text-[14px] text-grey_forty break-words ">
                  {companyDetails?.companyAddress}
                  {companyDetails?.companyCity &&
                    ` ${companyDetails?.companyCity}`}
                  {companyDetails?.zipCode && ` ${companyDetails?.zipCode}`}
                </p>
                <div className="flex items-center gap-2 text-grey_forty">
                  <h2 className="text-grey_forty text-[14px] font-ns_bold">
                    {t('Web')}:
                  </h2>
                  {companyDetails?.companyUrl ? (
                    <p
                      className="text-[14px] text-[#0091ff] overflow-hidden text-nowrap text-ellipsis w-full hover:underline cursor-pointer"
                      onClick={() => handleUrlClick(companyDetails?.companyUrl)}
                    >
                      {companyDetails?.companyUrl}
                    </p>
                  ) : (
                    <Skeleton className="w-full h-5" />
                  )}
                </div>
              </div>
            </div>
            <div className="">
              <Button
                className=" rounded-[3px] px-2 py-1 gap-2 mobile:w-full"
                variant="secondary"
                onClick={() => {
                  userCompany === companyDetails.companyName
                    ? pushWithLocale(`myfructidor/edit_profile`)
                    : pushWithLocale(
                        `/${companyIndustryType}/${companyDetails.friendlyUrl}/contact-us`
                      )
                }}
              >
                {userCompany === companyDetails.companyName ? (
                  <>
                    <Image
                      loading="lazy"
                      width={12}
                      height={15}
                      src="/assets/Icons/whiteEditPencil.svg"
                      alt=""
                    />
                    {t('Edit Profile')}
                  </>
                ) : (
                  t('Show Contact')
                )}
              </Button>
            </div>
            <div className="flex flex-col gap-5"></div>
            <div className="text-base pb-[8px] border-b border-solid border-[#f0f0f0] font-ns_semibold">
              {t('Additional Information')}
            </div>
            <div className="flex flex-col gap-3 text-[14px]">
              <div className="flex flex-col mobile:flex-row">
                <h2 className="text-[#747474] ">{t('Year of creation')}</h2>
                <span className="mobile:mx-2 hidden mobile:block">:</span>
                <p className="pt-[4px] text-grey_forty text-base font-ns_bold mobile:pt-0">
                  {companyDetails?.createdDate
                    ? companyDetails?.createdDate
                    : '-'}
                </p>
              </div>
              <div className="flex flex-col mobile:flex-row">
                <h2 className="text-[#747474] ">{t('Employees')}</h2>
                <span className="mobile:mx-2 hidden mobile:block">:</span>
                <p className="pt-[4px] text-grey_forty text-base font-ns_bold mobile:pt-0">
                  {companyDetails?.employees ? companyDetails?.employees : '-'}
                </p>
              </div>
              <div className="flex flex-col mobile:flex-row">
                <h2 className="text-[#747474] ">{t('Turnover')} €</h2>
                <span className="mobile:mx-2 hidden mobile:block">:</span>
                <p className="pt-[4px] text-grey_forty text-base font-ns_bold  mobile:pt-0">
                  {companyDetails?.turnover ? companyDetails?.turnover : '-'}
                </p>
              </div>
              <div className="flex flex-col mobile:flex-row">
                <h2 className="text-[#747474] ">{t('Registration Number')}</h2>
                <span className="mobile:mx-2 hidden mobile:block">:</span>
                <p className="pt-[4px] text-grey_forty text-base font-ns_bold mobile:pt-0">
                  {companyDetails?.registrationNumber
                    ? companyDetails?.registrationNumber
                    : '-'}
                </p>
              </div>
              <div className="flex flex-col mobile:flex-row">
                <h2 className="text-[#747474] ">{t('Languages')}</h2>
                <span className="mobile:mx-2 hidden mobile:block">:</span>
                <div className="flex mt-[3px] flex-wrap  mobile:mt-0">
                  {companyDetails?.languages?.length > 0
                    ? companyDetails?.languages?.map(
                        (language: string, index: number) => (
                          <CustomErrorBoundary key={index}>
                            <p
                              key={index}
                              className="mr-[7px] mt-[7px] text-[14px] py-[3px] px-3 border border-solid border-[#eaeaea] bg-[#f4f4f4] rounded-[30px] mobile:mt-0"
                            >
                              {language}
                            </p>
                          </CustomErrorBoundary>
                        )
                      )
                    : '-'}
                </div>
              </div>

              <div className="w-[70px] h-[60px] bg-[#f5f5f5] absolute top-0 right-0 rounded-[0_19px_0_200px] ">
                {companyDetails?.subscriptionIcon &&
                  companyDetails?.subscriptionIcon !== 'Free' && (
                    <Image
                      loading="lazy"
                      width={27}
                      height={23.9}
                      src={`/assets/Icons/crown/${companyDetails?.subscriptionIcon}.svg`}
                      className="absolute left-[30px] top-[15px] transform: rotate(45deg)"
                      alt=""
                    />
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CompanyDetailCard
